import { PaymentProvider } from "@greenflagdate/shared";
import { Box, BoxProps } from "@chakra-ui/react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  Stripe,
  StripeElementChangeEvent,
  StripeElementStyle,
} from "@stripe/stripe-js";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isPostalCode } from "validator";
import { translateStripeCode } from "../../lib/payments/stripe/errorMap";
import { getStripe } from "../../lib/payments/stripe/stripeJs";
import {
  CardFieldsHandle,
  CardFieldsProps,
  GENERIC_PAYMENT_ERROR,
} from "../../lib/payments/types";
import { CardFormErrors, CardFormLayout } from "./CardFormLayout";

/**
 * Stripe hosts the card inputs in iframes of its own, so nothing about our
 * theme reaches them: the font has to be loaded into the frame separately, and
 * the colors and metrics handed over as plain CSS values. The same face the
 * page loads (see entry-points/index.html), at the one weight the `cardInput`
 * text style uses, so the iframes match the inputs around them.
 */
const POPPINS_CSS =
  "https://fonts.googleapis.com/css2?family=Poppins:wght@500&display=swap";
const ELEMENTS_OPTIONS = { fonts: [{ cssSrc: POPPINS_CSS }] };

type ElementKey = "number" | "expiry" | "cvc";

/** What each Stripe element last told us about itself. */
interface ElementState {
  complete: boolean;
  /** Stripe's own validation copy, e.g. "Your card number is invalid." */
  error?: string;
}

const UNTOUCHED: Record<ElementKey, ElementState> = {
  number: { complete: false },
  expiry: { complete: false },
  cvc: { complete: false },
};

/** Default copy for an element the user left blank or unfinished. */
const INCOMPLETE_COPY: Record<ElementKey, string> = {
  number: "Please enter your credit card number",
  expiry: "Expiration date invalid",
  cvc: "Please enter your credit card CVC",
};

/**
 * The card cluster's text, resolved to the CSS values Stripe's iframes need.
 *
 * Read off elements painted with the same tokens rather than copied from the
 * theme, so a palette change reaches the iframes without anyone remembering
 * to update a second copy here.
 */
interface ResolvedInk {
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  letterSpacing: string;
  color: string;
  placeholder: string;
  danger: string;
}

const useResolvedInk = () => {
  const inkRef = useRef<HTMLSpanElement>(null);
  const mutedRef = useRef<HTMLSpanElement>(null);
  const dangerRef = useRef<HTMLSpanElement>(null);
  const [ink, setInk] = useState<ResolvedInk | null>(null);

  useLayoutEffect(() => {
    if (!inkRef.current || !mutedRef.current || !dangerRef.current) {
      return;
    }
    const base = getComputedStyle(inkRef.current);
    setInk({
      fontFamily: base.fontFamily,
      fontSize: base.fontSize,
      fontWeight: base.fontWeight,
      letterSpacing: base.letterSpacing,
      color: base.color,
      placeholder: getComputedStyle(mutedRef.current).color,
      danger: getComputedStyle(dangerRef.current).color,
    });
  }, []);

  // Never displayed, only measured: `visibility` rather than `display` so
  // that the computed style is still resolved.
  const probes = (
    <Box
      aria-hidden
      position="absolute"
      visibility="hidden"
      pointerEvents="none"
    >
      <Box as="span" ref={inkRef} textStyle="cardInput" color="fg.default" />
      <Box as="span" ref={mutedRef} color="fg.muted" />
      <Box as="span" ref={dangerRef} color="bg.danger" />
    </Box>
  );

  return { ink, probes };
};

const elementStyle = (ink: ResolvedInk | null): StripeElementStyle | undefined =>
  ink
    ? {
        base: {
          fontFamily: ink.fontFamily,
          fontSize: ink.fontSize,
          fontWeight: ink.fontWeight,
          letterSpacing: ink.letterSpacing,
          color: ink.color,
          "::placeholder": { color: ink.placeholder },
        },
        invalid: { color: ink.danger },
      }
    : undefined;

/**
 * The cell one Stripe element sits in — the same box our own inputs take, so
 * the cluster measures the same whichever provider fills it. The element's
 * mount node is a block div; it has to be told to fill the row.
 */
const ElementSlot = (props: BoxProps) => (
  <Box
    display="flex"
    alignItems="center"
    h="52px"
    w="100%"
    px="14px"
    css={{ "& > div": { flex: 1, minWidth: 0 } }}
    {...props}
  />
);

interface InnerProps extends CardFieldsProps {
  /** `tokenize` found no SDK: the outer component starts another load. */
  onSdkMissing: () => void;
}

/**
 * The half of the fields that needs Stripe's hooks, and therefore has to sit
 * inside the `Elements` provider the outer component renders.
 */
const StripeCardFieldsInner = forwardRef<CardFieldsHandle, InnerProps>(
  ({ error, onCancel, onSdkMissing }, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const { ink, probes } = useResolvedInk();
    const style = useMemo(() => elementStyle(ink), [ink]);

    const [name, setName] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [elementState, setElementState] =
      useState<Record<ElementKey, ElementState>>(UNTOUCHED);
    const [errors, setErrors] = useState<CardFormErrors>();

    const track = useCallback(
      (key: ElementKey) => (event: StripeElementChangeEvent) => {
        setElementState((state) => ({
          ...state,
          [key]: { complete: event.complete, error: event.error?.message },
        }));
      },
      []
    );

    useImperativeHandle(ref, () => ({
      async tokenize() {
        // No SDK means no card inputs on the page: nothing to validate, and
        // nothing the field copy below could honestly say about inputs that
        // are not there. Report it, and start another load so the next
        // attempt can find them.
        const cardNumber = elements?.getElement(CardNumberElement);
        if (!stripe || !cardNumber) {
          onSdkMissing();
          setErrors({ authorize: "Unable to connect to payment processor" });
          return null;
        }

        const fieldErrors: CardFormErrors = {};

        // Stripe's own validation copy where it has spoken; ours where the
        // user simply has not finished.
        const elementError = (key: ElementKey) =>
          elementState[key].complete
            ? undefined
            : (elementState[key].error ?? INCOMPLETE_COPY[key]);
        fieldErrors.cardNumber = elementError("number");
        fieldErrors.expDate = elementError("expiry");
        fieldErrors.cvc = elementError("cvc");

        if (!name.length) {
          fieldErrors.name = "Name is required";
        }
        if (!postalCode.length) {
          fieldErrors.postalCode = "Zip code is required";
        } else if (!isPostalCode(postalCode, "US")) {
          fieldErrors.postalCode = "Please double check your zip code";
        }

        setErrors(fieldErrors);
        if (Object.values(fieldErrors).some(Boolean)) {
          return null;
        }

        // The number never reaches our code: Stripe reads it out of its own
        // iframe and hands back a PaymentMethod id, which is the token.
        const result = await stripe.createPaymentMethod({
          type: "card",
          card: cardNumber,
          billing_details: {
            name,
            address: { postal_code: postalCode, country: "US" },
          },
        });
        if (result.error || !result.paymentMethod) {
          setErrors({
            authorize:
              translateStripeCode(
                result.error?.decline_code ?? result.error?.code
              ) ??
              result.error?.message ??
              GENERIC_PAYMENT_ERROR,
          });
          return null;
        }

        return {
          token: { provider: PaymentProvider.Stripe, token: result.paymentMethod.id },
          postalCode,
        };
      },
    }));

    return (
      <>
        {probes}
        <CardFormLayout
          onCancel={onCancel}
          errors={error ? { ...errors, authorize: error } : errors}
          name={name}
          onNameChange={setName}
          postalCode={postalCode}
          onPostalCodeChange={setPostalCode}
          cardNumber={
            <ElementSlot pr="160px">
              <CardNumberElement
                options={{
                  style,
                  showIcon: false,
                  placeholder: "1234 1234 1234 1234",
                }}
                onChange={track("number")}
              />
            </ElementSlot>
          }
          expiry={
            <ElementSlot>
              <CardExpiryElement
                options={{ style, placeholder: "MM / YY" }}
                onChange={track("expiry")}
              />
            </ElementSlot>
          }
          cvc={
            <ElementSlot>
              <CardCvcElement
                options={{ style, placeholder: "CVC" }}
                onChange={track("cvc")}
              />
            </ElementSlot>
          }
        />
      </>
    );
  }
);

/**
 * Stripe card entry.
 *
 * Stripe Elements host the number, expiry and CVC in iframes the page cannot
 * read, so this adapter renders our layout around Stripe's inputs and asks
 * Stripe for a PaymentMethod when it is time to tokenize — the number never
 * passes through our code. Which is why the card UI belongs to the adapter
 * rather than to PaymentForm.
 */
export const StripeCardFields = forwardRef<CardFieldsHandle, CardFieldsProps>(
  (props, ref) => {
    // `Elements` takes null until the SDK is here and must never see the
    // instance change afterwards, so it is handed the instance itself once
    // loaded rather than a promise pinned at mount. A load that failed is
    // forgotten by `getStripe`, so `loadSdk` is a real retry — and it is
    // `tokenize` that asks for one and reports the failure, the same place
    // the Authorize.net fields report theirs.
    const [stripe, setStripe] = useState<Stripe | null>(null);
    const loadSdk = useCallback(() => {
      getStripe().then(setStripe, () => {
        /* noop — reported by tokenize, which asks for another load */
      });
    }, []);
    useEffect(loadSdk, [loadSdk]);

    return (
      <Elements stripe={stripe} options={ELEMENTS_OPTIONS}>
        <StripeCardFieldsInner ref={ref} {...props} onSdkMissing={loadSdk} />
      </Elements>
    );
  }
);
